import React, { useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Textarea,
  TextareaProps,
  Icon
} from '@chakra-ui/react'
import { FiZap, FiRefreshCw } from 'react-icons/fi'
import { apiConfig } from '../../config/api'

export type FieldType = 'ad_headline' | 'ad_body' | 'landing_page_headline' | 'landing_page_body' | 'general'

interface ImproveableTextareaProps extends Omit<TextareaProps, 'value' | 'onChange'> {
  value: string
  onChange: (value: string) => void
  context: string // Field identifier (e.g., "who_you_help", "hero_headline")
  fieldType: FieldType
  onboardingMode?: 'service' | 'product'
  minLengthForImprove?: number // Minimum characters before showing improve button
}

export const ImproveableTextarea: React.FC<ImproveableTextareaProps> = ({
  value,
  onChange,
  context,
  fieldType,
  onboardingMode,
  minLengthForImprove = 10,
  maxLength,
  ...textareaProps
}) => {
  const [improving, setImproving] = useState(false)
  const [improvedText, setImprovedText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleImprove = async () => {
    if (!value.trim() || value.trim().length < minLengthForImprove) {
      return
    }

    setImproving(true)
    setImprovedText(null)
    setError(null)

    try {
      const token = localStorage.getItem('firebase_id_token')
      if (!token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/onboarding/improve-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: value,
          context: context,
          field_type: fieldType,
          onboarding_mode: onboardingMode
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to improve text' }))
        throw new Error(errorData.detail || `HTTP ${response.status}`)
      }

      const data = await response.json()
      setImprovedText(data.improved_text)
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to improve text. Please try again.'
      setError(errorMessage)
      // Show error in console for debugging
      console.error('Failed to improve text:', err)
    } finally {
      setImproving(false)
    }
  }

  const handleAccept = () => {
    if (improvedText) {
      onChange(improvedText)
      setImprovedText(null)
    }
  }

  const handleKeepOriginal = () => {
    setImprovedText(null)
    setError(null)
  }

  const trimmedLength = value.trim().length
  const canImprove = trimmedLength >= minLengthForImprove && !improving && !improvedText
  const charCount = value.length
  const isOverLimit = maxLength ? charCount > maxLength : false
  const hasMinText = trimmedLength >= minLengthForImprove

  return (
    <VStack align="stretch" gap={2} width="full">
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        {...textareaProps}
      />

      <Box w="full">
        <HStack justify="space-between" align="center" w="full" flexWrap="wrap" gap={2}>
          <HStack gap={2} flex={1} minW={0}>
            {maxLength && (
              <Text fontSize="xs" color={isOverLimit ? 'red.500' : 'gray.400'}>
                {charCount}/{maxLength} characters
                {isOverLimit && ' (over limit)'}
              </Text>
            )}
            {error && (
              <Text fontSize="xs" color="red.500">
                {error}
              </Text>
            )}
          </HStack>
          <Button
            size="xs"
            variant="ghost"
            colorPalette="purple"
            onClick={handleImprove}
            loading={improving}
            disabled={!hasMinText || !!improvedText}
            leftIcon={<Icon as={FiZap} />}
            flexShrink={0}
            opacity={hasMinText && !improvedText ? 1 : 0.5}
          >
            Improve with AI
          </Button>
        </HStack>
      </Box>

      {improvedText && (
        <Box
          p={4}
          bg="purple.50"
          borderRadius="md"
          borderLeft="4px solid"
          borderColor="purple.500"
          mt={2}
        >
          <VStack align="stretch" gap={3}>
            <HStack justify="space-between" align="start">
              <HStack gap={2}>
                <Icon as={FiZap} color="purple.500" />
                <Text fontSize="sm" fontWeight="bold" color="purple.700">
                  Improved Text:
                </Text>
              </HStack>
              {maxLength && (
                <Text fontSize="xs" color={improvedText.length > maxLength ? 'red.500' : 'gray.500'}>
                  {improvedText.length}/{maxLength} characters
                </Text>
              )}
            </HStack>
            <Text fontSize="md" color="gray.800" whiteSpace="pre-wrap" lineHeight="tall">
              {improvedText}
            </Text>
            {improvedText.length > (maxLength || Infinity) && (
              <Text fontSize="xs" color="red.500">
                Warning: Improved text exceeds character limit
              </Text>
            )}
            <HStack gap={3}>
              <Button size="sm" colorPalette="purple" onClick={handleAccept}>
                Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                colorPalette="purple"
                onClick={handleImprove}
                loading={improving}
                leftIcon={<Icon as={FiRefreshCw} />}
              >
                Refresh
              </Button>
              <Button size="sm" variant="ghost" colorPalette="gray" onClick={handleKeepOriginal}>
                Keep Original
              </Button>
            </HStack>
          </VStack>
        </Box>
      )}
    </VStack>
  )
}


